import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { ActorRefFrom, waitFor } from 'xstate';

import { useApiClient } from '@eluve/api-client-provider';
import { SessionMachine } from '@eluve/apollo-client';
import { FCC, useToast } from '@eluve/components';
import { LoggerArgs, useExtendedLogger } from '@eluve/logger';
import { useSession, useTenantIdFromParams } from '@eluve/session-helpers';

// Bounded retry for the stale-access recheck. Thrown refresh = transient
// transport failure, not "no access" — retry with backoff. Resolved (201/403)
// is final, not retried.
const ACCESS_RECHECK_RETRY = {
  maxAttempts: 3,
  baseDelayMs: 500,
  maxDelayMs: 4000,
} as const;

export const TenantGuard: FCC<{
  client: ApolloClient<NormalizedCacheObject>;
  sessionActor: ActorRefFrom<SessionMachine>;
}> = ({ children, client, sessionActor }) => {
  const [machineInitialized, setMachineInitialized] = useState(false);
  // Recheck outcome when the claim says not provisioned (claim can be stale —
  // see refreshStaleTenantAccess). 'verified' = server rebuilt the claim from
  // the DB (still-missing tenant = genuinely not a member); 'failed' = no
  // verdict (transport error / 403 edge) — must NOT enter the provision dialog
  // or it loops.
  //
  // Keyed by tenantId: this guard instance is reused across client-side tenant
  // changes (A→B, no remount). A verdict computed for A must not be acted on
  // during B's first render — see the `recheckStatus` derivation below.
  // [ENG-4763]
  const [accessRecheck, setAccessRecheck] = useState<{
    tenantId: string | null;
    status: 'idle' | 'refreshing' | 'verified' | 'failed';
  }>({ tenantId: null, status: 'idle' });
  const toast = useToast();

  const apiClient = useApiClient();
  const tenantSwitchFailed = useSelector(sessionActor, (state) =>
    state.matches('TenantSwitchFailed'),
  );

  const { pathname, search } = useLocation();
  const { appointmentId } = useParams<{ appointmentId?: string }>();
  const redirectToDefaultTenant = pathname.includes('/admin') ? '/admin' : '/';

  const tenantParamsId = useTenantIdFromParams();

  const allowedTenants = useSession().allowedTenantIds;
  const tenantLoggerArgs: LoggerArgs = useMemo(
    () => ({ name: 'TenantGuard', metadata: { tenantId: tenantParamsId } }),
    [tenantParamsId],
  );

  const logger = useExtendedLogger(tenantLoggerArgs);

  useMount(() => {
    // We don't want to actually mount the ApolloClient until we know that the
    // session machine has acknolwedged the tenant change. This is only relevant
    // for users that have multiple tenants and are switching between them
    // in multiple tabs.
    waitFor(
      sessionActor,
      (state) => state.context.desiredTenantId === tenantParamsId,
    ).then(() => setMachineInitialized(true));
  });

  if (!tenantParamsId) {
    throw new Error('Expected tenant id in route params in TenantGuard');
  }

  // allowedTenants is a fresh array on every render (useSession re-parses the
  // JWT each time), so derive a stable primitive before using it as an effect
  // dependency — depending on the array would re-run the effect every render
  // and re-send CHANGE_TENANT, which clears the JWT and forces a token re-fetch.
  const isProvisionedForTenant = allowedTenants.includes(tenantParamsId);

  // A recheck verdict only applies to the tenant it was computed for. When the
  // route param changes client-side (no remount), the leftover verdict from the
  // previous tenant reads as 'idle' so we hold and re-verify the new tenant
  // instead of redirecting on a stale verdict. [ENG-4763]
  const recheckStatus =
    accessRecheck.tenantId === tenantParamsId ? accessRecheck.status : 'idle';

  useEffect(
    // Provide new values to the session management machine so that
    // it can reconcile to the desired tenant
    function updateTenantIdForSession() {
      // NOTE: this session-reconciliation effect is mirrored in
      // apps/web-vite/src/routes/app/tenant/$tenantId/TenantGuard.tsx — keep the
      // isProvisionedForTenant guard below in sync across both copies.
      //
      // Don't attempt a token/tenant switch on the redirect path. When the user
      // isn't provisioned for tenantParamsId, the render-time guard below bounces
      // them to the org list to provision. Sending CHANGE_TENANT here would drive
      // the session machine through a 403 switch-tenant call into TenantSwitchFailed,
      // which then breaks the Apollo token fetch on the destination page.
      if (!isProvisionedForTenant) return;

      sessionActor.send({
        type: 'CHANGE_TENANT',
        desiredTenantId: tenantParamsId,
      });

      sessionActor.send({ type: 'SUPPLY_LOGGER', logger });

      sessionActor.send({
        type: 'SUPPLY_RESET_LOGIC',
        reset: async () => {
          const res = await apiClient.auth.createTenantSession({
            body: {
              tenantId: tenantParamsId,
            },
          });

          if (res.status !== 201) {
            throw new Error(
              `Failed to switch tenant session. Status: ${res.status}`,
            );
          }
        },
      });
    },
    [apiClient.auth, logger, tenantParamsId, isProvisionedForTenant],
  );

  useEffect(
    // Claim may be stale: another admin can add us (writing the tenant_users
    // row) without touching this session's token. Refresh allowedTenantIds —
    // runs in our session, rebuilds the claim from the DB. Success →
    // isProvisionedForTenant flips true; else fall through to the deny path.
    // Keyed on tenantParamsId not accessRecheck so the 'refreshing'
    // self-transition can't re-run the effect and cancel its own retry.
    function refreshStaleTenantAccess() {
      if (isProvisionedForTenant) return;

      let cancelled = false;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      setAccessRecheck({ tenantId: tenantParamsId, status: 'refreshing' });

      const attempt = (n: number): void => {
        // A retry scheduled before cleanup still fires after it; bail before
        // sending a request that would resolve against a stale tenant.
        if (cancelled) return;
        void apiClient.auth
          .refreshAllowedTenants({ body: {} })
          // Resolved = definitive. 201 = claim rebuilt ('verified'); other
          // status (403 edge) = no verdict ('failed'), don't loop on it.
          .then((res) => {
            if (!cancelled) {
              setAccessRecheck({
                tenantId: tenantParamsId,
                status: res.status === 201 ? 'verified' : 'failed',
              });
            }
          })
          // Thrown = transient transport failure; retry with backoff, else fail.
          .catch(() => {
            if (cancelled) return;
            if (n < ACCESS_RECHECK_RETRY.maxAttempts) {
              const delay = Math.min(
                ACCESS_RECHECK_RETRY.baseDelayMs * Math.pow(2, n - 1),
                ACCESS_RECHECK_RETRY.maxDelayMs,
              );
              retryTimer = setTimeout(() => attempt(n + 1), delay);
            } else {
              setAccessRecheck({ tenantId: tenantParamsId, status: 'failed' });
            }
          });
      };

      attempt(1);

      // Cancel pending retries on unmount/tenant change so a stale chain can't
      // resolve against another tenant, and clear any scheduled retry so it
      // can't fire a request after this effect is torn down.
      return () => {
        cancelled = true;
        if (retryTimer) clearTimeout(retryTimer);
      };
    },
    [apiClient.auth, isProvisionedForTenant, tenantParamsId],
  );

  useEffect(function transitionToIdleSessionOnUnmount() {
    return () => {
      sessionActor.send({ type: 'SESSION_COMPONENT_UNMOUNTED' });
    };
  }, []);

  useEffect(
    // A tenant switch fails server-authoritatively (403) when the user's
    // membership was revoked but the front-token's allowedTenantIds claim is
    // still stale. A *failed* switch does not rebuild the claim (the server
    // throws before re-minting the token), so the revoked tenant would keep
    // appearing as switchable until the next ~95s token refresh. Rebuild the
    // claim now so it drops out immediately. [ENG-4763]
    function selfHealClaimOnSwitchFailure() {
      if (!tenantSwitchFailed) return;

      // Best-effort: the claim still self-heals on the next token refresh.
      void apiClient.auth
        .refreshAllowedTenants({ body: {} })
        .catch(() => undefined);
    },
    [tenantSwitchFailed, apiClient.auth],
  );

  if (!isProvisionedForTenant) {
    // Still verifying — hold rendering, don't treat a stale token as denial.
    if (recheckStatus === 'idle' || recheckStatus === 'refreshing') {
      return null;
    }

    // No verdict (transport failure / 403 edge): claim not authoritatively
    // refreshed. Don't bounce to the provision dialog — its DB-based auto-nav
    // sees the row and sends us back, looping. Dead-end home with a retry hint.
    if (recheckStatus === 'failed') {
      toast.error('Error', {
        id: `tenant-access-recheck-failed-${tenantParamsId}`,
        description:
          'Unable to verify access to that organization. Please try again.',
      });

      return <Navigate to={redirectToDefaultTenant} />;
    }

    // 'verified': claim rebuilt and still not a member → genuinely
    // unprovisioned. Provision dialog is safe here (DB agrees, won't auto-nav/loop).
    // Appointment deep-links keep their existing prompt (QuickProvisionDialog).
    // Every other tenant page redirects to the org list carrying the tenant id
    // and the page the user was trying to reach, so TenantProvisionDialog can
    // offer to provision and return them there.
    const redirectTo = appointmentId
      ? `/admin/organizations?goToAppointmentId=${appointmentId}`
      : `/admin/organizations?provisionTenantId=${tenantParamsId}&returnTo=${encodeURIComponent(
          pathname + search,
        )}`;

    return <Navigate to={redirectTo} replace />;
  }

  if (tenantSwitchFailed) {
    toast.error('Error', {
      id: `tenant-switch-failed-${tenantParamsId}`,
      description: 'Unable to switch to that organization',
    });

    return <Navigate to={redirectToDefaultTenant} />;
  }

  // Wait for the session machine to be in a state where it has
  // acknowledged the correct tenant ID
  if (!machineInitialized) {
    return null;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
